// Central registry of every language the platform can present.
//
// Adding a language is a two-step process:
//   1. Add an entry here (set `enabled: true` once a locale module exists).
//   2. Drop a `src/lib/i18n/locales/<code>.ts` module (hand-authored for the
//      primary languages, AI-generated for the rest via scripts/i18n).
//
// `dir` is authored per-language so direction never has to be inferred from a
// hard-coded `language === 'ar'` check (see ./direction.ts).
//
// `complete` marks locales whose `legacy` catalog fully covers the English
// source keyset. The locale-coverage test only *enforces* completeness for
// `complete: true` languages; partially-generated locales fall back to English
// per-key via i18next's `fallbackLng` until the generator fills them in.

export type TextDirection = "ltr" | "rtl";

export interface LanguageMeta {
  /** BCP-47-ish code used as the i18next language + localStorage value. */
  code: string;
  /** Endonym — shown in the switcher in the language's own script. */
  nativeName: string;
  /** English name — used for search / accessibility labels. */
  englishName: string;
  /** Writing direction. */
  dir: TextDirection;
  /** Whether the locale is shipped (has a module) and shown in the switcher. */
  enabled: boolean;
  /** Whether the `legacy` catalog fully covers the English keyset. */
  complete: boolean;
}

/**
 * The full target set — curated for the platform's investor markets (Gulf,
 * China, Singapore, Japan, Korea, India, plus major European languages).
 * Only `enabled` entries appear in the language switcher.
 */
export const LANGUAGES: LanguageMeta[] = [
  { code: "ar", nativeName: "العربية", englishName: "Arabic", dir: "rtl", enabled: true, complete: true },
  { code: "en", nativeName: "English", englishName: "English", dir: "ltr", enabled: true, complete: true },
  // Staged locales: the engine, locale modules, catalogs and lazy importers are
  // all in place, but each stays `enabled: false` (hidden from the switcher)
  // until its full `legacy` catalog is generated. A delegated agent fills one
  // language (scripts/i18n/generate.mjs → catalog/<code>.legacy.json), then
  // flips BOTH `complete: true` and `enabled: true` in the same PR. See
  // docs/handoff/open/i18n-language-fill.md.
  { code: "zh", nativeName: "简体中文", englishName: "Chinese (Simplified)", dir: "ltr", enabled: true, complete: true },
  { code: "zh-TW", nativeName: "繁體中文", englishName: "Chinese (Traditional)", dir: "ltr", enabled: true, complete: true },
  { code: "ms", nativeName: "Bahasa Melayu", englishName: "Malay", dir: "ltr", enabled: true, complete: true },
  { code: "id", nativeName: "Bahasa Indonesia", englishName: "Indonesian", dir: "ltr", enabled: true, complete: true },
  { code: "ja", nativeName: "日本語", englishName: "Japanese", dir: "ltr", enabled: true, complete: true },
  { code: "ko", nativeName: "한국어", englishName: "Korean", dir: "ltr", enabled: true, complete: true },
  { code: "hi", nativeName: "हिन्दी", englishName: "Hindi", dir: "ltr", enabled: true, complete: true },
  { code: "ta", nativeName: "தமிழ்", englishName: "Tamil", dir: "ltr", enabled: true, complete: true },
  { code: "ur", nativeName: "اردو", englishName: "Urdu", dir: "rtl", enabled: true, complete: true },
  { code: "fa", nativeName: "فارسی", englishName: "Persian", dir: "rtl", enabled: true, complete: true },
  { code: "fr", nativeName: "Français", englishName: "French", dir: "ltr", enabled: true, complete: true },
  { code: "de", nativeName: "Deutsch", englishName: "German", dir: "ltr", enabled: true, complete: true },
  { code: "es", nativeName: "Español", englishName: "Spanish", dir: "ltr", enabled: true, complete: true },
  { code: "pt", nativeName: "Português", englishName: "Portuguese", dir: "ltr", enabled: true, complete: true },
  { code: "ru", nativeName: "Русский", englishName: "Russian", dir: "ltr", enabled: true, complete: true },
  { code: "tr", nativeName: "Türkçe", englishName: "Turkish", dir: "ltr", enabled: true, complete: true },
  { code: "th", nativeName: "ไทย", englishName: "Thai", dir: "ltr", enabled: true, complete: true },
  { code: "vi", nativeName: "Tiếng Việt", englishName: "Vietnamese", dir: "ltr", enabled: true, complete: true },
];

/** Languages shown in the switcher / accepted by `setLanguage`. */
export const ENABLED_LANGUAGES = LANGUAGES.filter((l) => l.enabled);

/** Fast lookup by code. */
export const LANGUAGE_BY_CODE: Record<string, LanguageMeta> = Object.fromEntries(
  LANGUAGES.map((l) => [l.code, l]),
);

/** The default language (Arabic-first). */
export const DEFAULT_LANGUAGE = "ar";

/** The fallback when a key is missing in the active locale. */
export const FALLBACK_LANGUAGE = "en";

export function isEnabledLanguage(code: string): boolean {
  return LANGUAGE_BY_CODE[code]?.enabled ?? false;
}
